<template>
  <v-row
    no-gutters
    class="h-100"
    justify="space-between"
  >
    <v-col cols="12" offset="0" md="4" offset-md="4" class="d-flex flex-column justify-center align-center">
      <v-card
        class="w-100 rounded-lg"
      >
        <v-card-title>{{ $t('resetPassword.title') }}</v-card-title>
        <v-card-text>
          <v-form
            ref="loginForm"
            v-model="valid"
          >
            <v-row>
              <v-col
                cols="12"
                class="pb-0"
              >
                <v-text-field
                  v-model="form.password"
                  :append-icon="form.passwordShow ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="form.passwordShow ? 'text' : 'password'"
                  name="input-10-1"
                  :label="$t('resetPassword.password')"
                  outlined
                  dense
                  single-line
                  validate-on-blur
                  :rules="passwordRules"
                  :disabled="autoLoginLoading"
                  @click:append="form.passwordShow = !form.passwordShow"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                class="pt-0"
              >
                <CheckPasswordStrength
                  v-if="form.password.length > 3"
                  :value="form.password"
                />
              </v-col>
              <v-col
                cols="12"
              >
                <v-text-field
                  v-model="form.confirmPassword"
                  :append-icon="form.confirmPasswordShow ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="form.confirmPasswordShow ? 'text' : 'password'"
                  name="input-10-1"
                  :label="$t('resetPassword.confirmPassword')"
                  outlined
                  dense
                  single-line
                  validate-on-blur
                  :rules="confrimPasswordRules"
                  :disabled="autoLoginLoading"
                  @click:append="form.confirmPasswordShow = !form.confirmPasswordShow"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4 mb-4">
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-btn color="info" outlined :disabled="autoLoginLoading" @click="generatePassword">
                {{ $t('resetPassword.generatePassword') }}
              </v-btn>
            </v-col>
            <v-col
              cols="12"
              md="8"
            >
              <div class="d-flex justify-end">
                <v-btn color="secondary" outlined class="mr-2" :disabled="autoLoginLoading" @click="$router.push({ name: 'Login' })">
                  {{ $t('resetPassword.cancel') }}
                </v-btn>
                <v-btn color="primary" :disabled="submitLoading || autoLoginLoading" :loading="submitLoading" @click="submitHandle">
                  {{ $t('resetPassword.submit') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-col>
    <TwoFactorMethod
      v-model="twoFactorMethodDialog"
      :loginData="loginData"
      :twoFactorMethods="twoFactorMethods"
      @close="closeTwoFactorMethodDialog()"
    />
    <ConfirmConsentDialog
      :value="confirmConsentDialog"
      :email="userEmail"
      :password="userPassword"
    />
  </v-row>
</template>

<script>
import services from '@/services'
import { generateString } from '@/utils/helpers'
import CheckPasswordStrength from '@/components/CheckPasswordStrength'
import TwoFactorMethod from './dialogs/TwoFactorMethod.vue'
import ConfirmConsentDialog from '@/components/dialogs/ConfirmConsentDialog'
import completeAuthMixin from '@/mixins/completeAuthMixin'

export default {
  name: 'ResetPassword',
  mixins: [completeAuthMixin],
  components: {
    CheckPasswordStrength,
    TwoFactorMethod,
    ConfirmConsentDialog
  },
  data () {
    return {
      form: {
        password: '',
        passwordShow: false,
        confirmPassword: '',
        confirmPasswordShow: false
      },
      submitLoading: false,
      autoLoginLoading: false,
      valid: true,
      twoFactorMethodDialog: false,
      twoFactorMethods: {
        phoneNumber: null,
        email: null
      },
      confirmConsentDialog: false,
      userEmail: '',
      userPassword: '',
      passwordRules: [
        v => !!v || this.$t('validation.passwordRequired'),
        (v) => v.length >= 8 || this.$t('validation.passwordInvalidLength')
      ],
      confrimPasswordRules: [
        v => !!v || this.$t('validation.confirmPasswordRequired'),
        v => v === this.form.password || this.$t('validation.passwordsMustMatch')
      ]
    }
  },
  computed: {
    loginData () {
      return {
        email: this.userEmail,
        password: this.userPassword
      }
    }
  },
  created () {
    this.logoutMobileDevice()
    this.handleAutoLogin()
  },
  methods: {
    generatePassword () {
      const newPassword = `${generateString()}${generateString()}`
      this.form.password = newPassword
      this.form.confirmPassword = newPassword
    },
    async submitHandle () {
      this.$refs.loginForm.validate()
      if (!this.valid) {
        return
      }
      this.submitLoading = true
      try {
        const { data } = await services.post('Authentication/SetNewPassword', {
          password: this.form.password,
          email: sessionStorage.getItem('email'),
          resetCode: sessionStorage.getItem('resetCode'),
          deviceId: localStorage.getItem('deviceId') || null,
          isWeb: true
        })
        this.userEmail = sessionStorage.getItem('email') || ''
        this.userPassword = this.form.password
        sessionStorage.removeItem('email')
        sessionStorage.removeItem('resetCode')
        sessionStorage.removeItem('expiryDate')
        await this.handleAuthenticationResponse(data)
      } catch (e) {}
      this.submitLoading = false
    },
    async handleAuthenticationResponse (data) {
      if (data.twoFactorAuthRequired) {
        this.twoFactorMethods = {
          phoneNumber: data.phoneNumber,
          email: data.email
        }
        this.twoFactorMethodDialog = true
        return
      }

      if (data.token) {
        await this.completeAuthentication(data)
        return
      }

      if (!data.requiredAgreementsAccepted) {
        this.confirmConsentDialog = true
      }
    },
    closeTwoFactorMethodDialog () {
      this.twoFactorMethodDialog = false
    },
    logoutMobileDevice () {
      if (this.$route.name !== 'ResetPasswordMobile') {
        return
      }
      if (this.$store.getters.isLoggedIn) {
        this.$store.dispatch('logout', true)
      }
      sessionStorage.setItem('email', this.$route.query.email)
      sessionStorage.setItem('resetCode', this.$route.query.resetCode)
    },
    async handleAutoLogin () {
      if (this.$route.name !== 'ResetPasswordAutoLogin') {
        return
      }

      if (this.$store.getters.isLoggedIn) {
        await this.$store.dispatch('logout', true)
      }

      const email = this.decodeBase64Param(this.$route.query.u)
      const password = this.decodeBase64Param(this.$route.query.p)

      if (!email || !password) {
        await this.$router.push({ name: 'Login' })
        return
      }

      this.autoLoginLoading = true
      try {
        const { data } = await services.post('Authentication/Login', {
          email,
          password,
          deviceId: localStorage.getItem('deviceId') || null,
          isWeb: true
        })

        if (data.resetCode) {
          sessionStorage.setItem('resetCode', data.resetCode)
          sessionStorage.setItem('email', email)
        } else {
          await this.$router.push({ name: 'Login' })
        }
      } catch (e) {
        await this.$router.push({ name: 'Login' })
      }
      this.autoLoginLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
